import React, { useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";

/* ── Values data ─────────────────────────────────────────────── */
const valueGroups = [
  {
    icon: "flaticon-stethoscope",
    color: "#1b74bb",
    lightColor: "#e8f2fb",
    heading: "Our Relationship with the People We Serve",
    items: [
      "The patient always comes first. We are dedicated to patient care.",
      "We treat each person with respect and dignity.",
      "We are compassionate listeners. We hear the issues of our patients, respect them, and do everything in our power to help.",
      "We provide patient-centered service. People can expect polite, friendly, helpful staff members who relate to each person as an individual, recognizing their history, relationships, culture, and needs.",
      "We keep ourselves well educated so that we can apply new developments in our fields, using innovative approaches to health care and customer service.",
      "We seek diversity in our staff and value their abilities to enhance communication with patients, and actively engage in multiple cultures in the community.",
      "We seek direction from patients through their guidance as members of our Board using multiple methods.",
      "We believe that patients deserve timely access to health care and that our systems should reflect this value.",
    ],
  },
  {
    icon: "flaticon-hospital",
    color: "#0d4f8a",
    lightColor: "#e2edf8",
    heading: "Our Approach to Health Care",
    items: [
      "We care for the whole person, see the complexity of each person's life, and believe that addressing a broad range of human needs is the best way to improve health.",
      "We continuously examine the services we provide and what is needed in the community, looking for gaps in the health care system and filling them.",
      "We believe that providing high quality, accessible health care is our reason for being. We seek to be a model for other community health centers.",
      "We use a team approach to providing health care, and involve the patient as part of our team.",
    ],
  },
  {
    icon: "flaticon-heart-1",
    color: "#1b40a0",
    lightColor: "#e4eaf7",
    heading: "Our Relationship with the Community",
    items: [
      "We have a commitment to serving the community and providing open access to Maroof International Hospital for all community members.",
      "Involvement by our staff in the community enhances our ability to provide effective health care.",
      "Partnerships with education, government, and other community organizations will multiply the resources and effectiveness of our work.",
      "By instilling confidence in our patients, they will become positive forces in the community and contribute to the health of others.",
    ],
  },
  {
    icon: "flaticon-first-aid",
    color: "#0e1c3d",
    lightColor: "#e0e4ef",
    heading: "Our Work Environment",
    items: [
      "In all we do, we actively pursue excellence and search for the next level of accomplishment.",
      "We take pride in our work and create a dynamic, forward-moving, innovative organization.",
      "Our integrity and ethics will never be compromised.",
      "We are good stewards of limited resources so that we can most effectively meet the many needs of our customers and the community.",
      "We are as respectful, friendly, helpful, and supportive to one another as we are to our patients.",
      "Teamwork is central to our work. We each take the responsibility to contribute effectively to teams.",
      "We recognize and appreciate the contributions and accomplishments of individuals and teams.",
      "Fun and humor are healthy for us and for our patients. We intentionally incorporate fun into the hospital environment.",
    ],
  },
];

/* ── Accordion item ──────────────────────────────────────────── */
const AccordionGroup = ({ group, index, isOpen, onToggle }) => (
  <div
    style={{
      borderRadius: "16px",
      overflow: "hidden",
      border: `1px solid ${isOpen ? group.color + "40" : "#e8edf5"}`,
      boxShadow: isOpen ? `0 8px 32px ${group.color}18` : "0 2px 12px rgba(0,0,0,0.05)",
      transition: "box-shadow .3s, border-color .3s",
      background: "#fff",
    }}
  >
    {/* Header */}
    <button
      onClick={() => onToggle(index)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "18px",
        padding: "22px 28px",
        background: isOpen ? `linear-gradient(90deg, ${group.color}12 0%, transparent 100%)` : "#fff",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        transition: "background .3s",
      }}
    >
      <div
        style={{
          width: "50px",
          height: "50px",
          borderRadius: "14px",
          background: isOpen ? `linear-gradient(135deg, ${group.color} 0%, ${group.color}cc 100%)` : group.lightColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background .3s",
          boxShadow: isOpen ? `0 6px 18px ${group.color}40` : "none",
        }}
      >
        <i className={group.icon} style={{ fontSize: "22px", color: isOpen ? "#fff" : group.color }} />
      </div>

      <span
        style={{
          flex: 1,
          fontSize: "17px",
          fontWeight: 700,
          color: isOpen ? group.color : "#0e1c3d",
          lineHeight: 1.3,
          transition: "color .3s",
        }}
      >
        {group.heading}
      </span>

      <div
        style={{
          width: "34px",
          height: "34px",
          borderRadius: "50%",
          background: isOpen ? group.color : "#f0f5fb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background .3s, transform .3s",
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
        }}
      >
        <i className="fal fa-angle-down" style={{ fontSize: "15px", color: isOpen ? "#fff" : "#555" }} />
      </div>
    </button>

    {/* Body */}
    {isOpen && (
      <div
        style={{
          padding: "4px 28px 28px",
          borderTop: `1px solid ${group.color}20`,
        }}
      >
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "12px" }}>
          {group.items.map((item, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "14px",
                fontSize: "14.5px",
                lineHeight: 1.75,
                color: "#555",
              }}
            >
              <span
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  background: group.lightColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              >
                <i className="fas fa-check" style={{ fontSize: "10px", color: group.color }} />
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

/* ── Main Component ──────────────────────────────────────────── */
const VisionMissionMain = ({ asSection = false }) => {
  const Tag = asSection ? "div" : "main";
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = (i) => setOpenIndex(openIndex === i ? -1 : i);

  return (
    <Tag>
      {!asSection && <Breadcrumb title="Our Vision &amp; Mission" subTitle="Vision / Mission" />}

      {/* ── Mission & Vision Cards ─────────────────────────── */}
      <section className="pt-120 pb-80">
        <div className="container">
          {/* Section label */}
          <div className="row justify-content-center mb-60 wow animate__fadeInUp" data-wow-duration=".9s" data-wow-delay=".2s">
            <div className="col-xl-7 col-lg-9 text-center">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "14px" }}>
                <span style={{ display: "inline-block", width: "36px", height: "3px", background: "#1b74bb", borderRadius: "2px" }} />
                <span style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "2.5px", textTransform: "uppercase", color: "#1b74bb" }}>
                  Our Purpose
                </span>
                <span style={{ display: "inline-block", width: "36px", height: "3px", background: "#1b74bb", borderRadius: "2px" }} />
              </div>
              <h2 style={{ fontSize: "clamp(26px, 3vw, 40px)", fontWeight: 800, color: "#0e1c3d", lineHeight: 1.25, margin: 0 }}>
                What Drives <span style={{ color: "#1b74bb" }}>Maroof International Hospital</span>
              </h2>
            </div>
          </div>

          {/* Mission + Vision side by side */}
          <div className="row g-4 mb-20">
            {/* Mission */}
            <div className="col-xl-6 col-lg-6 wow animate__fadeInLeft" data-wow-duration=".9s" data-wow-delay=".3s">
              <div
                style={{
                  height: "100%",
                  borderRadius: "20px",
                  overflow: "hidden",
                  position: "relative",
                  background: "linear-gradient(135deg, #0e1c3d 0%, #1b40a0 60%, #1b74bb 100%)",
                  padding: "48px 40px",
                  boxShadow: "0 20px 60px rgba(14,28,61,0.25)",
                }}
              >
                {/* Background glow */}
                <div
                  style={{
                    position: "absolute",
                    top: "-40px",
                    right: "-40px",
                    width: "200px",
                    height: "200px",
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "-60px",
                    left: "-30px",
                    width: "260px",
                    height: "260px",
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(27,116,187,0.2) 0%, transparent 70%)",
                    pointerEvents: "none",
                  }}
                />

                <div style={{ position: "relative", zIndex: 1 }}>
                  {/* Icon */}
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "18px",
                      background: "rgba(255,255,255,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "28px",
                      border: "1px solid rgba(255,255,255,0.2)",
                      backdropFilter: "blur(6px)",
                    }}
                  >
                    <i className="flaticon-stethoscope" style={{ fontSize: "30px", color: "#fff" }} />
                  </div>

                  <p
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "3px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.6)",
                      marginBottom: "10px",
                    }}
                  >
                    Our Mission
                  </p>

                  <h3
                    style={{
                      fontSize: "clamp(22px, 2.5vw, 30px)",
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.25,
                      marginBottom: "20px",
                    }}
                  >
                    Serving Every Patient, <span style={{ color: "#7ac9f8" }}>Every Day</span>
                  </h3>

                  <p
                    style={{
                      fontSize: "15.5px",
                      lineHeight: 1.85,
                      color: "rgba(255,255,255,0.82)",
                      margin: 0,
                    }}
                  >
                    To satisfy the needs of the patients by providing top-tier services and to reduce the disease burden through our quality
                    healthcare.
                  </p>

                  <div
                    style={{
                      marginTop: "32px",
                      width: "40px",
                      height: "3px",
                      background: "rgba(255,255,255,0.4)",
                      borderRadius: "2px",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="col-xl-6 col-lg-6 wow animate__fadeInRight" data-wow-duration=".9s" data-wow-delay=".4s">
              <div
                style={{
                  height: "100%",
                  borderRadius: "20px",
                  background: "#fff",
                  padding: "48px 40px",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.08)",
                  border: "1px solid #e8edf5",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Top accent bar */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "5px",
                    background: "linear-gradient(90deg, #1b74bb 0%, #4ab8ff 100%)",
                  }}
                />

                {/* Background pattern */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "-60px",
                    right: "-60px",
                    width: "220px",
                    height: "220px",
                    borderRadius: "50%",
                    background: "radial-gradient(circle, #e8f2fb 0%, transparent 70%)",
                    pointerEvents: "none",
                  }}
                />

                <div style={{ position: "relative", zIndex: 1 }}>
                  {/* Icon */}
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "18px",
                      background: "linear-gradient(135deg, #1b74bb 0%, #0d4f8a 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "28px",
                      boxShadow: "0 8px 24px rgba(27,116,187,0.3)",
                    }}
                  >
                    <i className="flaticon-hospital" style={{ fontSize: "30px", color: "#fff" }} />
                  </div>

                  <p
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "3px",
                      textTransform: "uppercase",
                      color: "#1b74bb",
                      marginBottom: "10px",
                    }}
                  >
                    Our Vision
                  </p>

                  <h3
                    style={{
                      fontSize: "clamp(22px, 2.5vw, 30px)",
                      fontWeight: 800,
                      color: "#0e1c3d",
                      lineHeight: 1.25,
                      marginBottom: "20px",
                    }}
                  >
                    Hospital of Choice for <span style={{ color: "#1b74bb" }}>Our Region</span>
                  </h3>

                  <p
                    style={{
                      fontSize: "15.5px",
                      lineHeight: 1.85,
                      color: "#555",
                      margin: 0,
                    }}
                  >
                    To be an exceptional healthcare service with contemporary technologies and innovative medical practices — a community in
                    which all people achieve their full potential for health and well-being across the lifespan. We work to be trusted by
                    patients, a valued partner in the community, and creators of positive change.
                  </p>

                  <div
                    style={{
                      marginTop: "32px",
                      width: "40px",
                      height: "3px",
                      background: "#1b74bb",
                      borderRadius: "2px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values Section ────────────────────────────────────── */}
      <section style={{ background: "#f7f9fc", padding: "96px 0 100px" }}>
        <div className="container">
          {/* Section header */}
          <div className="row justify-content-center mb-60 wow animate__fadeInUp" data-wow-duration=".9s" data-wow-delay=".2s">
            <div className="col-xl-8 col-lg-10 text-center">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "14px" }}>
                <span style={{ display: "inline-block", width: "36px", height: "3px", background: "#1b74bb", borderRadius: "2px" }} />
                <span style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "2.5px", textTransform: "uppercase", color: "#1b74bb" }}>
                  What We Stand For
                </span>
                <span style={{ display: "inline-block", width: "36px", height: "3px", background: "#1b74bb", borderRadius: "2px" }} />
              </div>
              <h2 style={{ fontSize: "clamp(26px, 3vw, 40px)", fontWeight: 800, color: "#0e1c3d", lineHeight: 1.25, marginBottom: "16px" }}>
                Our Core <span style={{ color: "#1b74bb" }}>Values</span>
              </h2>
              <p style={{ fontSize: "15.5px", lineHeight: 1.8, color: "#666", margin: 0 }}>
                Our work is guided by key values that reflect our commitment to patients, our community, our colleagues, and the standards of
                excellence we hold ourselves to every day.
              </p>
            </div>
          </div>

          {/* Accordion groups */}
          <div className="row justify-content-center wow animate__fadeInUp" data-wow-duration=".9s" data-wow-delay=".3s">
            <div className="col-xl-10 col-lg-12">
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {valueGroups.map((group, i) => (
                  <AccordionGroup key={i} group={group} index={i} isOpen={openIndex === i} onToggle={handleToggle} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Commitment Banner ─────────────────────────────────── */}
      <section
        style={{
          background: "linear-gradient(135deg, #0e1c3d 0%, #1b40a0 50%, #1b74bb 100%)",
          padding: "72px 0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <div
          style={{
            position: "absolute",
            top: "-80px",
            left: "-80px",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-100px",
            right: "-60px",
            width: "360px",
            height: "360px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.04)",
            pointerEvents: "none",
          }}
        />

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row align-items-center g-4">
            <div className="col-xl-8 col-lg-7 wow animate__fadeInLeft" data-wow-duration=".9s" data-wow-delay=".2s">
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.6)",
                  marginBottom: "10px",
                }}
              >
                Our Commitment
              </p>
              <h3 style={{ fontSize: "clamp(22px, 2.8vw, 34px)", fontWeight: 800, color: "#fff", lineHeight: 1.3, margin: 0 }}>
                Delivering Compassionate Care, <span style={{ color: "#7ac9f8" }}>Every Single Day</span>
              </h3>
            </div>
            <div className="col-xl-4 col-lg-5 text-lg-end wow animate__fadeInRight" data-wow-duration=".9s" data-wow-delay=".3s">
              <a
                href="/appointment"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  background: "#fff",
                  color: "#0e1c3d",
                  fontWeight: 700,
                  fontSize: "15px",
                  padding: "16px 32px",
                  borderRadius: "12px",
                  textDecoration: "none",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                  transition: "background .2s, color .2s, transform .2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#1b74bb";
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.transform = "translateY(-3px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fff";
                  e.currentTarget.style.color = "#0e1c3d";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <i className="flaticon-calendar" style={{ fontSize: "18px" }} />
                Book an Appointment
              </a>
            </div>
          </div>
        </div>
      </section>
    </Tag>
  );
};

export default VisionMissionMain;
